import { createSlice } from "@reduxjs/toolkit";
import axios from "../axios";
import toast from "../hooks/toast";
import { buildApiV3Url, buildApiV3UrlWithQuery } from "../shared/Utils";
import { CRUDOperation } from "../shared/constants";
import {
    parseGetAllObjResponse,
    parseGetObjResponse,
    parseSuccessResponse,
} from "../shared/responseUtils";

export const coverLetterSlice = createSlice({
    name: "coverLetter",
    initialState: {
        isloading: false,
        isTemplateTagsLoading: false,
        coverLetter: {
            vendorCoverLetters: [],
            customerCoverLetters: [],
            defaultCoverLetters: [],
        },
        coverLetterPreview: {},
        isCoverLetterPreviewLoading: false,
        isCoverLetterValid: false,
        noDataType: "",
        sortColumn: ["title", true],
        page: {
            data: [],
            paginate: {
                pageNumber: 0,
                startIndex: -1,
                endIndex: 0,
                rowsPerPage: 10,
                totalRecords: 0,
            },
        },
        templateTags: [],
        deliveryType: "E",
        coverLetterClassification: "",
        coverLetterClassificationReason: "",
        coverLetterSuggestedFixes: "",
        validationError: false,
    },
    reducers: {
        setCoverLetter: (state, action) => {
            if (action.payload.default) {
                state.coverLetter.defaultCoverLetters = action.payload.defaultCoverLetters;
            } else {
                if (action.payload.outgoing) {
                    state.coverLetter.vendorCoverLetters = action.payload.vendorCoverLetters;
                } else {
                    state.coverLetter.customerCoverLetters = action.payload.customerCoverLetters;
                }
            }
        },
        setLoading: (state, action) => {
            state.isloading = action.payload;
        },
        setCoverLetterPreview: (state, action) => {
            state.coverLetterPreview = action.payload;
        },
        setCoverLetterPreviewLoading: (state, action) => {
            state.isCoverLetterPreviewLoading = action.payload;
        },
        clearCoverLetterPreview: state => {
            state.coverLetterPreview = {};
        },
        setIsCoverLetterValid: (state, action) => {
            state.isCoverLetterValid = action.payload;
        },
        setPage: (state, action) => {
            state.page = action.payload;
        },
        setPageToNull: (state, action) => {
            state.page = {
                data: [],
                paginate: {
                    pageNumber: 0,
                    startIndex: -1,
                    endIndex: 0,
                    rowsPerPage: action?.payload,
                    totalRecords: 0,
                },
            };
        },
        setNoDataType: (state, action) => {
            state.noDataType = action.payload;
        },
        setSortColumn: (state, action) => {
            state.sortColumn = action.payload;
        },
        setIsTemplateTagsLoading: (state, action) => {
            state.isTemplateTagsLoading = action.payload;
        },
        setTemplateTags: (state, action) => {
            state.templateTags = action.payload;
        },
        setDeliveryType: (state, action) => {
            state.deliveryType = action.payload;
        },
        setCoverLetterClassification: (state, action) => {
            state.coverLetterClassification = action.payload;
        },
        setCoverLetterClassificationReason: (state, action) => {
            state.coverLetterClassificationReason = action.payload;
        },
        setCoverLetterSuggestedFixes: (state, action) => {
            state.coverLetterSuggestedFixes = action.payload;
        },
        setValidationError: (state, action) => {
            state.validationError = action.payload;
        },
    },
});

export const {
    setLoading,
    setCoverLetter,
    setCoverLetterPreview,
    setCoverLetterPreviewLoading,
    clearCoverLetterPreview,
    setIsCoverLetterValid,
    setPage,
    setPageToNull,
    setNoDataType,
    setSortColumn,
    setIsTemplateTagsLoading,
    setTemplateTags,
    setDeliveryType,
    setCoverLetterClassification,
    setCoverLetterClassificationReason,
    setCoverLetterSuggestedFixes,
    setValidationError,
} = coverLetterSlice.actions;

export const selectCoverLetter = state => {
    return state.coverLetter.coverLetter;
};

export const selectLoading = state => {
    return state.coverLetter.isloading;
};

export const selectCoverLetterPreview = state => {
    return state.coverLetter.coverLetterPreview;
};

export const selectPreviewLoading = state => {
    return state.coverLetter.isCoverLetterPreviewLoading;
};

export const selectIsCoverLetterValid = state => {
    return state.coverLetter.isCoverLetterValid;
};
export const selectPage = state => {
    return state.coverLetter.page;
};
export const selectSortColumn = state => {
    return state.coverLetter.sortColumn;
};
export const selectIsTemplateTagsLoading = state => {
    return state.coverLetter.isTemplateTagsLoading;
};

export const selectTemplateTags = state => {
    return state.coverLetter.templateTags;
};

export const selectDeliveryType = state => {
    return state.coverLetter.deliveryType;
};

export const selectCoverLetterClassification = state => {
    return state.coverLetter.coverLetterClassification;
};

export const selectCoverLetterClassificationReason = state => {
    return state.coverLetter.coverLetterClassificationReason;
};

export const selectCoverLetterSuggestedFixes = state => {
    return state.coverLetter.coverLetterSuggestedFixes;
};

export const selectValidationError = state => {
    return state.coverLetter.validationError;
};

const [showToast] = toast();

export const fetchDefaultCoverLetterAPI = () => async (dispatch, getState) => {
    const { coverLetter } = getState();
    if (coverLetter.isloading) {
        return;
    }

    dispatch(setLoading(true));
    const response = await axios.get(buildApiV3Url(`cover-letters?$top=100`), {
        withCredentials: true,
    });

    if (!parseSuccessResponse(CRUDOperation.ReadAll, response)?.recordFound) {
        dispatch(setCoverLetter({ defaultCoverLetters: [], default: true }));
    } else {
        const readAllResponse = parseGetAllObjResponse(response);
        dispatch(setCoverLetter({ defaultCoverLetters: readAllResponse.value, default: true }));
    }
    dispatch(setLoading(false));
};

export const fetchCoverLetterByIdAPI = id => async (dispatch, getState) => {
    const { coverLetter } = getState();
    if (coverLetter.isCoverLetterPreviewLoading) {
        return;
    }

    dispatch(setCoverLetterPreviewLoading(true));
    const response = await axios.get(buildApiV3Url(`cover-letters/${id}`), {
        withCredentials: true,
    });

    if (!parseSuccessResponse(CRUDOperation.Read, response)?.recordFound) {
        dispatch(setCoverLetterPreview({}));
    } else {
        const parseGetResponse = parseGetObjResponse(response?.data);
        dispatch(setCoverLetterPreview(parseGetResponse?.value));
    }
    dispatch(setCoverLetterPreviewLoading(false));
};

export const fetchOutgoingCoverLetterById = id => async (dispatch, getState) => {
    const { coverLetter } = getState();
    if (coverLetter.isCoverLetterPreviewLoading) {
        return;
    }

    dispatch(setCoverLetterPreviewLoading(true));
    const response = await axios.get(buildApiV3Url(`cover-letters/outgoing/${id}`), {
        withCredentials: true,
    });

    if (!parseSuccessResponse(CRUDOperation.Read, response)?.recordFound) {
        dispatch(setCoverLetterPreview({}));
    } else {
        const parseGetResponse = parseGetObjResponse(response?.data);
        dispatch(setCoverLetterPreview(parseGetResponse?.value));
    }

    dispatch(setCoverLetterPreviewLoading(false));
};

export const fetchCoverLetterByFiltersAPI = params => async (dispatch, getState) => {
    const { coverLetter } = getState();
    if (coverLetter.isCoverLetterPreviewLoading) {
        return;
    }

    dispatch(setCoverLetterPreviewLoading(true));
    const filterString = params ? `?$filter=${params}&$top=100` : "?$top=100";
    const response = await axios.get(buildApiV3Url(`cover-letters${filterString}`), {
        withCredentials: true,
    });

    if (!parseSuccessResponse(CRUDOperation.ReadAll, response)?.recordFound) {
        dispatch(setCoverLetter({ customerCoverLetters: [], outgoing: false }));
    } else {
        const readAllResponse = parseGetAllObjResponse(response);
        dispatch(setCoverLetter({ customerCoverLetters: readAllResponse.value, outgoing: false }));
    }
    dispatch(setCoverLetterPreviewLoading(false));
};

export const fetchOutgoingLettersByAPI = params => async (dispatch, getState) => {
    const { coverLetter } = getState();
    if (coverLetter.isCoverLetterPreviewLoading) {
        return;
    }

    dispatch(setCoverLetterPreviewLoading(true));
    const filterString = params ? `?$filter=${params}&$top=100` : "?$top=100";

    const response = await axios.get(
        buildApiV3Url(`cover-letters/outgoing${filterString}&$orderBy=title+ASC`),
        { withCredentials: true }
    );

    if (!parseSuccessResponse(CRUDOperation.ReadAll, response)?.recordFound) {
        dispatch(setCoverLetter({ vendorCoverLetters: [], outgoing: true }));
        dispatch(setCoverLetterPreview({}));
    } else {
        const parseGetAllResponse = parseGetAllObjResponse(response);
        dispatch(
            setCoverLetter({ vendorCoverLetters: parseGetAllResponse?.value, outgoing: true })
        );
        if (filterString !== "") {
            dispatch(setCoverLetterPreview(parseGetAllResponse?.value[0]));
        }
    }
    dispatch(setCoverLetterPreviewLoading(false));
};

export const fetchCoverLettersByPagingAPI =
    (pageSize, pageNumber, onLanding, paginateDetails) => async (dispatch, getState) => {
        const { coverLetter } = getState();
        const { rowsPerPage } = getState().session;
        const effectivePageSize = pageSize || rowsPerPage;
        const effectivePageNumber = pageNumber ?? 0;
        if (coverLetter.isCoverLetterPreviewLoading) {
            return;
        }
        dispatch(setCoverLetterPreviewLoading(true));
        const sort = `${coverLetter.sortColumn[0]} ${coverLetter.sortColumn[1] ? "ASC" : "DESC"}`;
        const apiURI = buildApiV3UrlWithQuery(
            `cover-letters?$filter`,
            "",
            "",
            effectivePageSize,
            effectivePageNumber,
            sort
        );
        const response = await axios.get(`${apiURI}`, {
            withCredentials: true,
        });
        if (!parseSuccessResponse(CRUDOperation.ReadAll, response)?.recordFound) {
            onLanding ? dispatch(setNoDataType("onLanding")) : dispatch(setNoDataType("onSearch"));
            dispatch(setPageToNull([]));
        } else {
            const parseGetAllResponse = parseGetAllObjResponse(response);
            dispatch(setNoDataType(""));
            const paginateData = paginateDetails
                ? {
                      pageNumber: paginateDetails.currentPage,
                      startIndex: paginateDetails.startIndex,
                      endIndex: paginateDetails.endIndex,
                      rowsPerPage: paginateDetails.rowsPerPage,
                      totalRecords: parseGetAllResponse?.count,
                  }
                : {
                      pageNumber: 1,
                      startIndex: 0,
                      endIndex: 19,
                      rowsPerPage: 10,
                      totalRecords: parseGetAllResponse?.count,
                  };
            dispatch(
                setPage({
                    data: parseGetAllResponse?.value,
                    paginate: paginateData,
                })
            );
        }
        dispatch(setCoverLetterPreviewLoading(false));
    };

export const fetchOutgoingCoverLettersByPagingAPI =
    (pageSize, pageNumber, onLanding, paginateDetails) => async (dispatch, getState) => {
        const { coverLetter } = getState();
        const effectivePageSize = pageSize ?? 10;
        const effectivePageNumber = pageNumber ?? 0;
        if (coverLetter.isCoverLetterPreviewLoading) {
            return;
        }
        dispatch(setCoverLetterPreviewLoading(true));
        const sort = `${coverLetter.sortColumn[0]} ${coverLetter.sortColumn[1] ? "ASC" : "DESC"}`;
        const apiURI = buildApiV3UrlWithQuery(
            `cover-letters/outgoing?$filter`,
            "",
            "",
            effectivePageSize,
            effectivePageNumber,
            sort
        );
        const response = await axios.get(`${apiURI}`, {
            withCredentials: true,
        });
        if (!parseSuccessResponse(CRUDOperation.ReadAll, response)?.recordFound) {
            dispatch(setPageToNull([]));
        } else {
            const readAllResponse = parseGetAllObjResponse(response);
            dispatch(setNoDataType(""));
            const paginateData = paginateDetails
                ? {
                      pageNumber: paginateDetails.currentPage,
                      startIndex: paginateDetails.startIndex,
                      endIndex: paginateDetails.endIndex,
                      rowsPerPage: paginateDetails.rowsPerPage,
                      totalRecords: readAllResponse?.count,
                  }
                : {
                      pageNumber: 1,
                      startIndex: 0,
                      endIndex: 19,
                      rowsPerPage: 10,
                      totalRecords: readAllResponse?.count,
                  };
            dispatch(
                setPage({
                    data: readAllResponse?.value,
                    paginate: paginateData,
                })
            );
        }
        dispatch(setCoverLetterPreviewLoading(false));
    };

export const validateCoverLetter = coverLetterContent => async (dispatch, getState) => {
    const { isloading } = getState().coverLetter;
    if (isloading) {
        return;
    }
    dispatch(setLoading(true));

    try {
        const response = await axios.post(
            buildApiV3Url("idp/coverletter-validation"),
            { coverLetterContent },
            {
                withCredentials: true,
            }
        );

        if (response.status === 200 && response?.data?.contentClassification) {
            const classification = response?.data?.contentClassification;
            const classificationReason = response?.data?.justification;
            const suggestedFixes = response?.data?.fixes;
            dispatch(setCoverLetterClassification(classification));
            dispatch(setCoverLetterClassificationReason(classificationReason));
            dispatch(setCoverLetterSuggestedFixes(suggestedFixes));
        } else {
            dispatch(setValidationError(true));
            showToast("error", "Failed to scan cover letter. Please try again.");
        }
    } catch (error) {
        const errorMessage =
            error?.response?.data?.message || "Failed to scan cover letter. Please try again.";
        showToast("error", errorMessage);
        dispatch(setValidationError(true));
    } finally {
        dispatch(setLoading(false));
    }
};

export const postCoverLetter = postData => async (dispatch, getState) => {
    const { coverLetter } = getState();
    if (coverLetter.isloading) {
        return [];
    }
    dispatch(setLoading(true));
    const response = await axios.post(
        buildApiV3Url(`cover-letters`),
        {
            title: postData.title,
            subject: postData.subject,
            htmlContent: postData.htmlContent,
            description: postData.description,
            scanResult: postData.scanResult,
        },
        {
            withCredentials: true,
        }
    );
    if (response.status === 201) {
        dispatch(setIsCoverLetterValid(true));
        showToast("success", "The template has been saved successfully.");
    }
    if (typeof response.data === "string") {
        dispatch(setIsCoverLetterValid(false));
        showToast("error", response.data);
    }
    dispatch(setLoading(false));
    return response;
};

export const postOutgoingCoverLetter = postData => async (dispatch, getState) => {
    const { coverLetter } = getState();
    if (coverLetter.isloading) {
        return [];
    }
    dispatch(setLoading(true));
    const response = await axios.post(
        buildApiV3Url(`cover-letters/outgoing`),
        {
            title: postData.title,
            subject: postData.subject,
            htmlContent: postData.htmlContent,
            description: postData.description,
            scanResult: postData.scanResult,
        },
        {
            withCredentials: true,
        }
    );
    if (response.status === 201) {
        dispatch(setIsCoverLetterValid(true));
        showToast("success", "The template has been saved successfully.");
    }
    if (typeof response.data === "string") {
        dispatch(setIsCoverLetterValid(false));
        showToast("error", response.data);
    }
    dispatch(setLoading(false));
    return response;
};

export const putCoverLetter = putData => async (dispatch, getState) => {
    const { coverLetter } = getState();
    if (coverLetter.isloading) {
        return [];
    }
    dispatch(setLoading(true));
    const response = await axios.put(buildApiV3Url(`cover-letters/${putData.id}`), putData, {
        withCredentials: true,
    });
    if (response) {
        if (typeof response.data === "object") {
            await dispatch(setIsCoverLetterValid(true));
            showToast("success", "The template has been updated successfully.");
        }
        if (typeof response.data === "string") {
            await dispatch(setIsCoverLetterValid(false));
            showToast("error", response.data);
        }
    }
    dispatch(setLoading(false));
    return response;
};

export const deleteCoverLetterAPI = coverLetterId => async (dispatch, getState) => {
    const { coverLetter } = getState();

    if (coverLetter.isloading) return;

    dispatch(setLoading(true));
    const response = await axios.delete(buildApiV3Url(`cover-letters/${coverLetterId}`), {
        withCredentials: true,
    });

    if (response?.data[0]?.description) {
        showToast("success", "The template has been deleted successfully.");
        dispatch(fetchCoverLettersByPagingAPI());
    }

    dispatch(setLoading(false));
};

export const deleteOutgoingCoverLetterAPI = coverLetterId => async (dispatch, getState) => {
    const { coverLetter } = getState();

    if (coverLetter.isloading) return;

    dispatch(setLoading(true));
    const response = await axios.delete(buildApiV3Url(`cover-letters/${coverLetterId}`), {
        withCredentials: true,
    });

    if (response?.data[0]?.description) {
        showToast("success", "The template has been deleted successfully.");
        dispatch(fetchOutgoingCoverLettersByPagingAPI());
    }

    dispatch(setLoading(false));
};

export const fetchTemplateTags = () => async (dispatch, getState) => {
    const { coverLetter } = getState();
    if (coverLetter.isTemplateTagsLoading) {
        return;
    }

    dispatch(setIsTemplateTagsLoading(true));
    const response = await axios.get(buildApiV3Url(`cover-letters/template-tags?documentType=1`), {
        withCredentials: true,
    });

    if (response && response.data) {
        dispatch(setTemplateTags(response.data.value));
    }
    dispatch(setIsTemplateTagsLoading(false));
};

export default coverLetterSlice.reducer;
